/**
 * Describes native values whose useful fields are not enumerable. Collection
 * contents and device labels are deliberately omitted: map keys can contain
 * labels or credentials, and a device label can identify its owner. Built-in
 * intrinsics bypass instance serialization and collection overrides.
 *
 * Returns `undefined` for values without a dedicated summary. These summaries
 * still need normal key and runtime-pattern redaction before transport.
 *
 * @example
 * summarizeNativeValue(new Set(['private'])); // {__type: 'Set', size: 1}
 */
export const summarizeNativeValue = (
    value: object,
): Record<string, unknown> | undefined => {
    if (value instanceof Date) {
        return {
            __type: 'Date',
            value: Number.isFinite(Date.prototype.getTime.call(value))
                ? Date.prototype.toISOString.call(value)
                : null,
        };
    }
    if (value instanceof Map) {
        return {__type: 'Map', size: Reflect.get(Map.prototype, 'size', value)};
    }
    if (value instanceof Set) {
        return {__type: 'Set', size: Reflect.get(Set.prototype, 'size', value)};
    }
    if (
        typeof MediaDeviceInfo !== 'undefined' &&
        value instanceof MediaDeviceInfo
    ) {
        return {
            __type: 'MediaDeviceInfo',
            kind: value.kind,
            deviceId: value.deviceId,
            groupId: value.groupId,
            hasLabel: value.label.length > 0,
        };
    }
    return undefined;
};
